import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { debounce } from "lodash";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Select,
  MenuItem,
  TextField,
  Collapse,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import {
  formatCurrency,
  formatCurrencyInput,
  processCurrencyInput,
  formatCurrencyOnBlur,
  getCurrencyValue,
} from "../../utils/formatters";
import { useTheme as useCustomTheme } from "../../contexts/ThemeContext";

// Add global style for select options
const selectOptionStyle = {
  color: "#000",
  backgroundColor: "#fff",
};

// Add asset category definitions
const ASSET_CATEGORIES = {
  CASH: "Cash & Equivalents",
  PROPERTY: "Property",
  INVESTMENTS: "Investments",
  OTHER: "Other",
};

// Add function to categorize assets by type
const categorizeAssetType = (assetTypeId, assetTypesData) => {
  if (!assetTypeId || !assetTypesData) return ASSET_CATEGORIES.OTHER;

  const assetType = assetTypesData.find(
    (type) => type.asset_type_id?.toString() === assetTypeId?.toString(),
  );

  if (!assetType) return ASSET_CATEGORIES.OTHER;

  const typeName = assetType.asset_type_name.toLowerCase();

  // Cash & Equivalents
  if (
    typeName.includes("cash") ||
    typeName.includes("checking") ||
    typeName.includes("savings") ||
    typeName.includes("money market") ||
    typeName.includes("cd") ||
    typeName.includes("certificate of deposit") ||
    typeName.includes("bank account")
  ) {
    return ASSET_CATEGORIES.CASH;
  }

  // Property
  if (
    typeName.includes("property") ||
    typeName.includes("real estate") ||
    typeName.includes("home") ||
    typeName.includes("house") ||
    typeName.includes("land") ||
    typeName.includes("residence")
  ) {
    return ASSET_CATEGORIES.PROPERTY;
  }

  // Investments
  if (
    typeName.includes("stock") ||
    typeName.includes("bond") ||
    typeName.includes("mutual fund") ||
    typeName.includes("etf") ||
    typeName.includes("ira") ||
    typeName.includes("401k") ||
    typeName.includes("retirement") ||
    typeName.includes("investment") ||
    typeName.includes("portfolio") ||
    typeName.includes("annuity") ||
    typeName.includes("securities")
  ) {
    return ASSET_CATEGORIES.INVESTMENTS;
  }

  return ASSET_CATEGORIES.INVESTMENTS;
};

// Add function to determine if an asset is a primary residence
const isPrimaryResidence = (asset) => {
  return asset.isPrimaryResidence === true || asset.id === "primary-residence";
};

// Add function to get display label for primary residence
const getPrimaryResidenceLabel = (asset) => {
  if (isPrimaryResidence(asset)) {
    return "Primary Residence";
  }
  return "";
};

const AssetAnalysisTable = ({
  assetsData = [],
  members = [],
  assetTypesData = [],
  taxQualificationsData = [],
  accountTypesData = [],
  financialInstitutionsData = [],
  ownerTypesData = [],
  onSave,
  config = {
    showDates: false,
    editable: true,
    title: "Asset Analysis",
    showRiskTolerance: true,
    showLastUpdated: true,
    showMember: true,
  },
}) => {
  const theme = useTheme();
  const { isDarkMode } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Initialize assets state with memoized assetsData
  const [assets, setAssets] = useState(() => assetsData);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isNewRow, setIsNewRow] = useState(false);
  const [showCustomInputs, setShowCustomInputs] = useState({});
  const isLocalEdit = useRef(false);
  const [expandedCategories, setExpandedCategories] = useState({
    [ASSET_CATEGORIES.CASH]: true,
    [ASSET_CATEGORIES.PROPERTY]: true,
    [ASSET_CATEGORIES.INVESTMENTS]: true,
    [ASSET_CATEGORIES.OTHER]: true,
  });

  // Update assets when assetsData changes and not in local edit
  useEffect(() => {
    if (!isLocalEdit.current) {
      setAssets(assetsData);
    }
  }, [assetsData]);

  // Memoize lookup maps for efficient access
  const memberMap = useMemo(() => {
    const map = new Map();
    members.forEach((member) => {
      const id = member.userId || member.user_id;
      if (id) {
        map.set(id.toString(), {
          name: `${member.firstName || member.first_name || ""} ${member.lastName || member.last_name || ""}`.trim(),
          isFormSubmitter: member.isFormSubmitter,
        });
      }
    });
    return map;
  }, [members]);

  const ownerTypeMap = useMemo(() => {
    const map = new Map();
    ownerTypesData.forEach((type) => {
      map.set(type.owner_type_id?.toString(), type.owner_type_name);
    });
    return map;
  }, [ownerTypesData]);

  const assetTypeMap = useMemo(() => {
    const map = new Map();
    assetTypesData.forEach((type) => {
      map.set(type.asset_type_id?.toString(), type.asset_type_name);
    });
    return map;
  }, [assetTypesData]);

  const taxQualificationMap = useMemo(() => {
    const map = new Map();
    taxQualificationsData.forEach((qual) => {
      map.set(
        qual.tax_qualification_id?.toString(),
        qual.tax_qualification_name,
      );
    });
    return map;
  }, [taxQualificationsData]);

  const accountTypeMap = useMemo(() => {
    const map = new Map();
    accountTypesData.forEach((type) => {
      map.set(type.account_type_id?.toString(), type.account_type_name);
    });
    return map;
  }, [accountTypesData]);

  const financialInstitutionMap = useMemo(() => {
    const map = new Map();
    financialInstitutionsData.forEach((inst) => {
      map.set(inst.institution_id?.toString(), inst.institution_name);
    });
    return map;
  }, [financialInstitutionsData]);

  // Process asset balance without any logging
  const processAssetBalance = useCallback((rawBalance) => {
    if (!rawBalance) return { parsedBalance: 0, type: "number" };
    const cleanedValue =
      typeof rawBalance === "string"
        ? rawBalance.replace(/[^0-9.-]/g, "")
        : rawBalance;
    const parsedBalance = parseFloat(cleanedValue);
    return {
      rawBalance,
      parsedBalance: isNaN(parsedBalance) ? 0 : parsedBalance,
      type: "number",
    };
  }, []);

  // Memoize processed assets
  const processedAssets = useMemo(() => {
    return assets.map((asset) => ({
      ...asset,
      processedBalance: processAssetBalance(asset.balance),
    }));
  }, [assets, processAssetBalance]);

  // Group assets by category efficiently using memoized maps
  const groupedAssets = useMemo(() => {
    const groups = {};
    Object.values(ASSET_CATEGORIES).forEach((category) => {
      groups[category] = processedAssets.filter((asset) => {
        const assetTypeName =
          assetTypeMap.get(asset.assetType)?.toLowerCase() || "";
        return (
          categorizeAssetType(asset.assetType, assetTypesData) === category
        );
      });
    });
    return groups;
  }, [processedAssets, assetTypesData, assetTypeMap]);

  // Calculate totals efficiently
  const categoryTotals = useMemo(() => {
    const totals = {};
    Object.entries(groupedAssets).forEach(([category, assets]) => {
      totals[category] = assets.reduce(
        (total, asset) => total + (asset.processedBalance?.parsedBalance || 0),
        0,
      );
    });
    return totals;
  }, [groupedAssets]);

  // Calculate grand total efficiently
  const totalAssets = useMemo(() => {
    return Object.values(categoryTotals).reduce(
      (total, categoryTotal) => total + (categoryTotal || 0),
      0,
    );
  }, [categoryTotals]);

  // Format currency helper function
  const formatAsCurrency = useCallback((value) => {
    if (!value && value !== 0) return "";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }, []);

  // Get owner or member name efficiently using memoized maps
  const getOwnerOrMemberName = useCallback(
    (asset) => {
      if (!asset) return "";

      if (isPrimaryResidence(asset)) {
        return getPrimaryResidenceLabel(asset);
      }

      // If we have an owner type, that takes precedence
      const ownerTypeName = ownerTypeMap.get(asset.ownerTypeId)?.toLowerCase();
      if (ownerTypeName) {
        if (ownerTypeName === "joint" || ownerTypeName === "household") {
          return "Joint";
        }

        // For 'self' or 'spouse', find the appropriate member
        if (ownerTypeName === "self") {
          // Find the primary member (form submitter)
          for (const [_, info] of memberMap) {
            if (info.isFormSubmitter) {
              return info.name;
            }
          }
        } else if (ownerTypeName === "spouse") {
          // Find the non-primary member (not form submitter)
          for (const [_, info] of memberMap) {
            if (!info.isFormSubmitter) {
              return info.name;
            }
          }
        }
      }

      // If we have a direct member ID match, use that
      if (asset.memberId) {
        const memberInfo = memberMap.get(asset.memberId.toString());
        if (memberInfo?.name) {
          return memberInfo.name;
        }
      }

      // If we have ownership structure ID of 2, it's joint
      if (asset.ownership_structure_id === 2) {
        return "Joint";
      }

      // If we can't determine ownership but have multiple members, default to Joint
      if (memberMap.size > 1) {
        return "Joint";
      }

      // If we have only one member, use their name
      if (memberMap.size === 1) {
        const [firstMember] = memberMap.values();
        return firstMember.name;
      }

      // Last resort
      return "Household";
    },
    [memberMap, ownerTypeMap],
  );

  // Handle input changes efficiently
  const handleInputChange = useCallback((id, field, value) => {
    setAssets((prevAssets) =>
      prevAssets.map((asset) =>
        asset.id === id ? { ...asset, [field]: value } : asset,
      ),
    );
  }, []);

  // Handle edit mode efficiently
  const handleEdit = useCallback((asset) => {
    setEditingId(asset.id);
    setEditForm(asset);
    setIsNewRow(false);
  }, []);

  // Handle cancel edit efficiently
  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditForm({});
    setIsNewRow(false);
  }, []);

  // Initialize data when props change
  useEffect(() => {
    if (isLocalEdit.current) return;

    // Map the backend format to the component's expected format
    const mappedAssets = assetsData.map((asset) => {
      // Find the corresponding member based on owner_type_id if possible
      let foundMemberId = null;

      // If we have members and owner_type_id, try to associate them
      if (asset.owner_type_id && members && members.length > 0) {
        // Get owner type name for this asset
        const ownerType = ownerTypesData.find(
          (ot) =>
            ot.owner_type_id?.toString() === asset.owner_type_id?.toString(),
        );

        if (ownerType) {
          // Find form submitter (primary) and spouse (secondary)
          const primaryMember = members.find((m) => m.isFormSubmitter === true);
          const spouseMember = members.find((m) => m.isFormSubmitter !== true);

          // Map owner type names to potential member types
          if (
            ownerType.owner_type_name.toLowerCase() === "self" &&
            primaryMember
          ) {
            // "Self" should map to the form submitter
            foundMemberId = primaryMember.userId;
          } else if (
            ownerType.owner_type_name.toLowerCase() === "spouse" &&
            spouseMember
          ) {
            // "Spouse" should map to the non-submitter
            foundMemberId = spouseMember.userId;
          }
          // Could add more mappings for other owner types if needed
        }
      }

      // If we already have a user_id, use that instead
      if (asset.user_id) {
        foundMemberId = asset.user_id;
      }

      // Format date from updated_at - check both updated_at and updatedAt (React camelCase)
      let lastUpdatedDate;

      if (asset.updated_at) {
        // Try to parse the database timestamp format
        try {
          lastUpdatedDate = new Date(asset.updated_at)
            .toISOString()
            .split("T")[0];
        } catch (e) {
          lastUpdatedDate = new Date().toISOString().split("T")[0];
        }
      } else if (asset.updatedAt) {
        try {
          lastUpdatedDate = new Date(asset.updatedAt)
            .toISOString()
            .split("T")[0];
        } catch (e) {
          lastUpdatedDate = new Date().toISOString().split("T")[0];
        }
      } else if (asset.last_updated) {
        lastUpdatedDate = asset.last_updated;
      } else if (asset.lastUpdated) {
        lastUpdatedDate = asset.lastUpdated;
      } else {
        lastUpdatedDate = new Date().toISOString().split("T")[0];
      }

      // Handle balance - ensure we get a proper number
      let balanceValue;
      const rawBalance =
        asset.balance ||
        asset.approximate_value ||
        asset.checking_savings_balance ||
        0;

      // Convert to number if it's a string
      if (typeof rawBalance === "string") {
        // Clean the string value (remove non-numeric chars except decimal point)
        const cleanedValue = rawBalance.replace(/[^0-9.]/g, "");
        balanceValue = parseFloat(cleanedValue);
      } else {
        balanceValue = parseFloat(rawBalance);
      }

      // If NaN, use 0
      if (isNaN(balanceValue)) balanceValue = 0;

      const mappedAsset = {
        ...asset,
        id: asset.id || asset.asset_id || `temp-${Date.now()}`,
        memberId: foundMemberId,
        financialInstitution:
          asset.financial_institution || asset.financialInstitution,
        financialInstitutionId:
          asset.financial_institution_id || asset.financialInstitutionId,
        assetType: asset.asset_type_id?.toString() || asset.assetType,
        taxQualification:
          asset.tax_qualification_id?.toString() || asset.taxQualification,
        accountType: asset.account_type_id?.toString() || asset.accountType,
        ownerTypeId: asset.owner_type_id?.toString() || asset.ownerTypeId,
        balance: balanceValue,
        riskTolerance:
          asset.risk_tolerance ||
          asset.riskTolerance ||
          asset.overall_portfolio_risk_number,
        lastUpdated: lastUpdatedDate,
        deleted: false,
        // Preserve ownership structure information
        ownership_structure_id: asset.ownership_structure_id,
        secondary_owner_user_id: asset.secondary_owner_user_id,
      };

      return mappedAsset;
    });

    setAssets(mappedAssets);
  }, [
    assetsData,
    members,
    assetTypesData,
    taxQualificationsData,
    accountTypesData,
    financialInstitutionsData,
    ownerTypesData,
  ]);

  // Toggle custom input fields
  const toggleCustomInput = (id, field, value) => {
    setShowCustomInputs((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: value,
      },
    }));
  };

  // Debounced save function
  const debouncedSave = debounce(() => {
    onSave(assets.filter((asset) => !asset.deleted));
    isLocalEdit.current = false;
  }, 500);

  // Save changes
  const handleSaveEdit = () => {
    isLocalEdit.current = true;

    // Process the form data
    const processedEditForm = { ...editForm };

    // If this is a primary residence, maintain special flags
    if (isPrimaryResidence(processedEditForm)) {
      processedEditForm.isPrimaryResidence = true;
      processedEditForm.financialInstitution = "Primary Residence";
      processedEditForm.financialInstitutionId = null;
    }

    // Convert balance from formatted to numeric if needed
    if (processedEditForm.balanceFormatted) {
      // Get actual numeric value from the formatted amount
      processedEditForm.balance = getCurrencyValue(
        processedEditForm.balanceFormatted,
      );
      // Remove the formatted version to avoid confusion in data storage
      delete processedEditForm.balanceFormatted;
    } else if (processedEditForm.balance) {
      // Make sure balance is a number
      processedEditForm.balance =
        parseFloat(String(processedEditForm.balance).replace(/[$,]/g, "")) || 0;
    }

    // Generate current timestamp for updated_at field when making changes
    const currentTimestamp = new Date().toISOString();
    const currentDate = currentTimestamp.split("T")[0];

    // Only assign a new updated_at timestamp if this is a new asset or changes were made
    const isNewAsset =
      processedEditForm.id &&
      processedEditForm.id.toString().startsWith("temp-");
    const item = assets.find((a) => a.id === editingId);

    // Should we update the timestamp?
    const shouldUpdateTimestamp =
      isNewAsset ||
      (item &&
        (item.balance !== processedEditForm.balance ||
          item.memberId !== processedEditForm.memberId ||
          item.ownerTypeId !== processedEditForm.ownerTypeId ||
          item.assetType !== processedEditForm.assetType ||
          item.taxQualification !== processedEditForm.taxQualification ||
          item.accountType !== processedEditForm.accountType ||
          item.financialInstitutionId !==
            processedEditForm.financialInstitutionId ||
          item.riskTolerance !== processedEditForm.riskTolerance));

    // Make sure database field names are correctly set
    const updatedData = assets.map((item) =>
      item.id === editingId
        ? {
            ...processedEditForm,
            // Ensure we keep the original data formats that might be expected elsewhere
            approximate_value: processedEditForm.balance,
            user_id: processedEditForm.memberId,
            owner_type_id: processedEditForm.ownerTypeId,
            financial_institution_id: processedEditForm.financialInstitutionId,
            form_id: processedEditForm.formId || item.formId, // Preserve the form_id
            updated_at: shouldUpdateTimestamp
              ? currentTimestamp
              : item.updated_at || currentTimestamp, // Only update if changes were made
            lastUpdated: shouldUpdateTimestamp
              ? currentDate
              : item.lastUpdated || currentDate, // Update lastUpdated to match
          }
        : item,
    );

    setAssets(updatedData);
    onSave(updatedData.filter((asset) => !asset.deleted));
    setEditingId(null);
    setIsNewRow(false);
    isLocalEdit.current = false;
  };

  // Add new asset
  const handleAdd = () => {
    const existingAssets = [...assets];
    const formId = existingAssets.length > 0 ? existingAssets[0].formId : null;

    // Create a unique temporary ID for the new asset
    const newId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    // Create timestamps for the new asset
    const currentTimestamp = new Date().toISOString();
    const currentDate = currentTimestamp.split("T")[0]; // Just the date part

    setAssets([
      ...existingAssets,
      {
        id: newId,
        memberId: "",
        financialInstitution: "",
        financialInstitutionId: "",
        assetType: "",
        taxQualification: "",
        accountType: "",
        ownerTypeId: "",
        formId: formId,
        balance: 0,
        riskTolerance: "",
        lastUpdated: currentDate,
        updated_at: currentTimestamp,
        created_at: currentTimestamp,
        deleted: false,
      },
    ]);

    setEditingId(newId);
    setIsNewRow(true);

    // Scroll to the bottom of the table to see the new asset
    setTimeout(() => {
      const table = document.getElementById("asset-analysis-table");
      if (table) {
        table.scrollIntoView({ behavior: "smooth", block: "end" });
      }
    }, 100);
  };

  // Delete asset
  const handleDelete = (id) => {
    isLocalEdit.current = true;

    const updatedData = assets.map((item) =>
      item.id === id ? { ...item, deleted: true } : item,
    );

    setAssets(updatedData);
    onSave(updatedData.filter((asset) => !asset.deleted));
    isLocalEdit.current = false;
  };

  // Get asset type name from assetTypesData
  const getAssetTypeName = (typeId) => {
    if (!typeId) return "Unknown";

    const assetType = assetTypesData.find(
      (type) => type.asset_type_id?.toString() === typeId?.toString(),
    );
    return assetType ? assetType.asset_type_name : "Unknown";
  };

  // Get tax qualification name from taxQualificationsData
  const getTaxQualificationName = (qualId) => {
    if (!qualId) return "Unknown";

    const taxQualification = taxQualificationsData.find(
      (qual) => qual.tax_qualification_id?.toString() === qualId?.toString(),
    );
    return taxQualification
      ? taxQualification.tax_qualification_name
      : "Unknown";
  };

  // Get account type name from accountTypesData
  const getAccountTypeName = (acctId, asset) => {
    // If we don't have account types data, return a default value
    if (!accountTypesData || accountTypesData.length === 0) {
      // For cash-type assets, return a default based on owner type
      if (asset && (asset.assetType === "9" || asset.assetType === "13")) {
        const ownerType = ownerTypesData.find(
          (ot) =>
            ot.owner_type_id?.toString() === asset.ownerTypeId?.toString(),
        );

        const ownerTypeName = ownerType
          ? ownerType.owner_type_name.toLowerCase()
          : "";
        const isIndividualOwner =
          ownerTypeName === "self" ||
          ownerTypeName === "spouse" ||
          ownerTypeName === "individual";

        return isIndividualOwner
          ? "Individual (Non-Qualified)"
          : "Joint (Non-Qualified)";
      }
      return "Unknown";
    }

    // Special handling for Cash or Checking/Savings accounts with null account type
    if (
      asset &&
      (asset.assetType === "9" || asset.assetType === "13") &&
      !acctId
    ) {
      const ownerType = ownerTypesData.find(
        (ot) => ot.owner_type_id?.toString() === asset.ownerTypeId?.toString(),
      );

      const ownerTypeName = ownerType
        ? ownerType.owner_type_name.toLowerCase()
        : "";
      const isIndividualOwner =
        ownerTypeName === "self" ||
        ownerTypeName === "spouse" ||
        ownerTypeName === "individual";

      return isIndividualOwner
        ? "Individual (Non-Qualified)"
        : "Joint (Non-Qualified)";
    }

    if (!acctId) return "Unknown";

    // Simple direct lookup by ID
    const accountType = accountTypesData.find(
      (acct) => acct.account_type_id?.toString() === acctId?.toString(),
    );

    return accountType ? accountType.account_type_name : "Unknown";
  };

  // Get financial institution name from financialInstitutionsData
  const getFinancialInstitutionName = (instId, asset) => {
    // If this specific asset is a primary residence, show that
    if (isPrimaryResidence(asset)) {
      return "Primary Residence";
    }

    if (!instId) return "";

    const institution = financialInstitutionsData.find(
      (inst) => inst.institution_id?.toString() === instId?.toString(),
    );
    return institution ? institution.institution_name : "";
  };

  // Get owner type name from ownerTypesData
  const getOwnerTypeName = (typeId) => {
    if (!typeId) return "";

    const ownerType = ownerTypesData.find(
      (type) => type.owner_type_id?.toString() === typeId?.toString(),
    );
    return ownerType ? ownerType.owner_type_name : "";
  };

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Card 
        elevation={2} 
        sx={{ 
          mb: 4, 
          borderRadius: 2,
          bgcolor: isDarkMode ? 'background.paper' : 'background.default',
          color: isDarkMode ? 'text.primary' : 'inherit',
          border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : 'none'
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            {config.title && (
              <Typography 
                variant="h5" 
                component="h2" 
                fontWeight="600"
                sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}
              >
                {config.title}
              </Typography>
            )}
            {config.editable && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdd}
                sx={{
                  borderRadius: 20,
                  background: isDarkMode 
                    ? "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)"
                    : "linear-gradient(45deg, #1976d2 30%, #2196f3 90%)",
                  boxShadow: isDarkMode
                    ? "0 3px 5px 2px rgba(25, 118, 210, .5)"
                    : "0 3px 5px 2px rgba(33, 150, 243, .3)",
                  "&:hover": {
                    background: isDarkMode
                      ? "linear-gradient(45deg, #0d47a1 30%, #1565c0 90%)"
                      : "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s",
                }}
              >
                Add Asset
              </Button>
            )}
          </Box>

          {/* Group assets by category */}
          {Object.values(ASSET_CATEGORIES).map((category) => {
            const categoryAssets = assets.filter(
              (asset) =>
                !asset.deleted &&
                categorizeAssetType(asset.assetType, assetTypesData) ===
                  category,
            );

            if (categoryAssets.length === 0) return null;

            const categoryTotal = categoryAssets.reduce(
              (total, asset) => total + parseFloat(asset.balance || 0),
              0,
            );

            return (
              <Paper
                key={category}
                elevation={1}
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  overflow: "hidden",
                  bgcolor: isDarkMode ? 'background.paper' : 'background.default',
                  border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : 'none'
                }}
              >
                {/* Category Header */}
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    bgcolor: isDarkMode ? 'background.paper' : 'background.default',
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: isDarkMode ? 'action.hover' : 'background.default',
                    },
                    borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : undefined
                  }}
                  onClick={() => toggleCategory(category)}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconButton
                      size="small"
                      sx={{ 
                        mr: 1,
                        color: isDarkMode ? 'text.primary' : 'inherit'
                      }}
                      aria-label={
                        expandedCategories[category] ? "collapse" : "expand"
                      }
                    >
                      {expandedCategories[category] ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                    <Typography 
                      variant="h6" 
                      component="div"
                      sx={{ 
                        color: isDarkMode ? 'text.primary' : 'inherit'
                      }}
                    >
                      {category}
                    </Typography>
                    <Chip
                      label={formatAsCurrency(categoryTotal)}
                      sx={{ 
                        ml: 2,
                        bgcolor: isDarkMode ? 'primary.dark' : 'primary.main',
                        color: 'white',
                        '& .MuiChip-label': {
                          color: 'white'
                        }
                      }}
                      variant="filled"
                    />
                  </Box>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: isDarkMode ? 'text.secondary' : 'text.primary'
                    }}
                  >
                    {categoryAssets.length}{" "}
                    {categoryAssets.length === 1 ? "Asset" : "Assets"}
                  </Typography>
                </Box>

                {/* Category Content */}
                <Collapse in={expandedCategories[category]} timeout="auto">
                  <TableContainer 
                    sx={{ 
                      bgcolor: isDarkMode ? 'background.paper' : 'background.default',
                      borderRadius: 1,
                      '& .MuiTable-root': {
                        bgcolor: 'transparent'
                      }
                    }}
                  >
                    <Table 
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        '& .MuiTableCell-root': {
                          borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : undefined
                        },
                        '& .MuiTableRow-root:hover': {
                          bgcolor: isDarkMode ? 'action.hover' : undefined
                        }
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          {config.showMember && (
                            <TableCell 
                              sx={{ 
                                color: isDarkMode ? 'text.primary' : 'inherit',
                                bgcolor: isDarkMode ? 'background.paper' : 'inherit',
                                borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : undefined
                              }}
                            >
                              Member
                            </TableCell>
                          )}
                          <TableCell 
                            sx={{ 
                              color: isDarkMode ? 'text.primary' : 'inherit',
                              bgcolor: isDarkMode ? 'background.paper' : 'inherit',
                              borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : undefined
                            }}
                          >
                            Institution
                          </TableCell>
                          <TableCell 
                            sx={{ 
                              color: isDarkMode ? 'text.primary' : 'inherit',
                              bgcolor: isDarkMode ? 'background.paper' : 'inherit',
                              borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : undefined
                            }}
                          >
                            Asset Type
                          </TableCell>
                          <TableCell 
                            sx={{ 
                              color: isDarkMode ? 'text.primary' : 'inherit',
                              bgcolor: isDarkMode ? 'background.paper' : 'inherit',
                              borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : undefined
                            }}
                          >
                            Tax Qualification
                          </TableCell>
                          <TableCell 
                            sx={{ 
                              color: isDarkMode ? 'text.primary' : 'inherit',
                              bgcolor: isDarkMode ? 'background.paper' : 'inherit',
                              borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : undefined
                            }}
                          >
                            Account Type
                          </TableCell>
                          <TableCell 
                            align="right"
                            sx={{ 
                              color: isDarkMode ? 'text.primary' : 'inherit',
                              bgcolor: isDarkMode ? 'background.paper' : 'inherit',
                              borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : undefined
                            }}
                          >
                            Balance
                          </TableCell>
                          {config.showRiskTolerance && (
                            <TableCell 
                              align="center"
                              sx={{ 
                                color: isDarkMode ? 'text.primary' : 'inherit',
                                bgcolor: isDarkMode ? 'background.paper' : 'inherit',
                                borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : undefined
                              }}
                            >
                              Risk
                            </TableCell>
                          )}
                          {config.showLastUpdated && (
                            <TableCell 
                              sx={{ 
                                color: isDarkMode ? 'text.primary' : 'inherit',
                                bgcolor: isDarkMode ? 'background.paper' : 'inherit',
                                borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : undefined
                              }}
                            >
                              Last Updated
                            </TableCell>
                          )}
                          {config.editable && (
                            <TableCell 
                              align="right"
                              sx={{ 
                                color: isDarkMode ? 'text.primary' : 'inherit',
                                bgcolor: isDarkMode ? 'background.paper' : 'inherit',
                                borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : undefined
                              }}
                            >
                              Actions
                            </TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {categoryAssets.map((asset) => (
                          <TableRow
                            key={asset.id}
                            hover
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                              bgcolor:
                                editingId === asset.id
                                  ? "action.selected"
                                  : "inherit",
                            }}
                          >
                            {editingId === asset.id ? (
                              // Editing Mode
                              <>
                                {config.showMember && (
                                  <TableCell>
                                    {isPrimaryResidence(asset) ? (
                                      <TextField
                                        disabled
                                        value="Joint / Household"
                                        fullWidth
                                        size="small"
                                      />
                                    ) : (
                                      <Select
                                        value={
                                          editForm.memberId ||
                                          (editForm.ownerTypeId
                                            ? "owner-" + editForm.ownerTypeId
                                            : "")
                                        }
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          if (value.startsWith("owner-")) {
                                            const ownerTypeId = value.replace(
                                              "owner-",
                                              "",
                                            );
                                            setEditForm({
                                              ...editForm,
                                              memberId: "",
                                              ownerTypeId: ownerTypeId,
                                            });
                                          } else {
                                            let ownerTypeId = "";
                                            const selectedMember = members.find(
                                              (m) =>
                                                m.userId?.toString() ===
                                                value?.toString(),
                                            );
                                            const primaryMember = members.find(
                                              (m) => m.isFormSubmitter === true,
                                            );
                                            const spouseMember = members.find(
                                              (m) => m.isFormSubmitter !== true,
                                            );

                                            if (
                                              selectedMember &&
                                              primaryMember &&
                                              selectedMember.userId ===
                                                primaryMember.userId
                                            ) {
                                              const selfType =
                                                ownerTypesData.find(
                                                  (ot) =>
                                                    ot.owner_type_name.toLowerCase() ===
                                                    "self",
                                                );
                                              ownerTypeId = selfType
                                                ? selfType.owner_type_id
                                                : "";
                                            } else if (
                                              selectedMember &&
                                              spouseMember &&
                                              selectedMember.userId ===
                                                spouseMember.userId
                                            ) {
                                              const spouseType =
                                                ownerTypesData.find(
                                                  (ot) =>
                                                    ot.owner_type_name.toLowerCase() ===
                                                    "spouse",
                                                );
                                              ownerTypeId = spouseType
                                                ? spouseType.owner_type_id
                                                : "";
                                            }

                                            setEditForm({
                                              ...editForm,
                                              memberId: value,
                                              ownerTypeId,
                                            });
                                          }
                                        }}
                                        fullWidth
                                        size="small"
                                      >
                                        <MenuItem value="">
                                          Select Owner
                                        </MenuItem>
                                        {members.map((member) => (
                                          <MenuItem
                                            key={member.userId}
                                            value={member.userId}
                                          >
                                            {member.firstName} {member.lastName}
                                          </MenuItem>
                                        ))}
                                        <Divider />
                                        {ownerTypesData.map((ot) => (
                                          <MenuItem
                                            key={`owner-${ot.owner_type_id}`}
                                            value={`owner-${ot.owner_type_id}`}
                                          >
                                            {ot.owner_type_name}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    )}
                                  </TableCell>
                                )}
                                <TableCell>
                                  {isPrimaryResidence(asset) ? (
                                    <TextField
                                      disabled
                                      value="Primary Residence"
                                      fullWidth
                                      size="small"
                                      sx={{
                                        '& .MuiInputBase-input': {
                                          color: isDarkMode ? 'text.primary' : 'inherit',
                                        },
                                        '& .MuiOutlinedInput-root': {
                                          '& fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                                          },
                                          '&:hover fieldset': {
                                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                                          },
                                          '&.Mui-focused fieldset': {
                                            borderColor: isDarkMode ? 'primary.light' : 'primary.main',
                                          },
                                        },
                                      }}
                                    />
                                  ) : (
                                    <Select
                                      value={editForm.financialInstitutionId || ""}
                                      onChange={(e) =>
                                        setEditForm({
                                          ...editForm,
                                          financialInstitutionId: e.target.value,
                                        })
                                      }
                                      fullWidth
                                      size="small"
                                      sx={{
                                        color: isDarkMode ? 'text.primary' : 'inherit',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                          borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                          borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                          borderColor: isDarkMode ? 'primary.light' : 'primary.main',
                                        },
                                        '& .MuiSelect-icon': {
                                          color: isDarkMode ? 'text.primary' : 'inherit',
                                        },
                                      }}
                                      MenuProps={{
                                        PaperProps: {
                                          sx: {
                                            bgcolor: isDarkMode ? 'background.paper' : 'background.default',
                                            '& .MuiMenuItem-root': {
                                              color: isDarkMode ? 'text.primary' : 'inherit',
                                              '&:hover': {
                                                bgcolor: isDarkMode ? 'action.hover' : undefined,
                                              },
                                              '&.Mui-selected': {
                                                bgcolor: isDarkMode ? 'action.selected' : undefined,
                                              },
                                            },
                                          },
                                        },
                                      }}
                                    >
                                      <MenuItem value="">Select Institution</MenuItem>
                                      {financialInstitutionsData.map((inst) => (
                                        <MenuItem
                                          key={inst.institution_id}
                                          value={inst.institution_id}
                                        >
                                          {inst.institution_name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {isPrimaryResidence(asset) ? (
                                    <TextField
                                      disabled
                                      value="Primary Residence"
                                      fullWidth
                                      size="small"
                                    />
                                  ) : (
                                    <Select
                                      value={editForm.assetType || ""}
                                      onChange={(e) =>
                                        setEditForm({
                                          ...editForm,
                                          assetType: e.target.value,
                                        })
                                      }
                                      fullWidth
                                      size="small"
                                    >
                                      <MenuItem value="">Select Type</MenuItem>
                                      {assetTypesData.map((type) => (
                                        <MenuItem
                                          key={type.asset_type_id}
                                          value={type.asset_type_id}
                                        >
                                          {type.asset_type_name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {isPrimaryResidence(asset) ? (
                                    <TextField
                                      disabled
                                      value="-"
                                      fullWidth
                                      size="small"
                                    />
                                  ) : (
                                    <Select
                                      value={editForm.taxQualification || ""}
                                      onChange={(e) =>
                                        setEditForm({
                                          ...editForm,
                                          taxQualification: e.target.value,
                                        })
                                      }
                                      fullWidth
                                      size="small"
                                    >
                                      <MenuItem value="">
                                        Select Qualification
                                      </MenuItem>
                                      {taxQualificationsData.map((qual) => (
                                        <MenuItem
                                          key={qual.tax_qualification_id}
                                          value={qual.tax_qualification_id}
                                        >
                                          {qual.tax_qualification_name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {isPrimaryResidence(asset) ? (
                                    <TextField
                                      disabled
                                      value="-"
                                      fullWidth
                                      size="small"
                                    />
                                  ) : (
                                    <Select
                                      value={editForm.accountType || ""}
                                      onChange={(e) =>
                                        setEditForm({
                                          ...editForm,
                                          accountType: e.target.value,
                                        })
                                      }
                                      fullWidth
                                      size="small"
                                    >
                                      <MenuItem value="">
                                        Select Account Type
                                      </MenuItem>
                                      {accountTypesData.map((acct) => (
                                        <MenuItem
                                          key={acct.account_type_id}
                                          value={acct.account_type_id}
                                        >
                                          {acct.account_type_name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    value={
                                      editForm.balanceFormatted ||
                                      formatCurrencyInput(editForm.balance || 0)
                                    }
                                    onChange={(e) => {
                                      const rawValue = e.target.value;
                                      const processedValue =
                                        processCurrencyInput(rawValue);
                                      setEditForm({
                                        ...editForm,
                                        balance: processedValue,
                                        balanceFormatted:
                                          formatCurrencyInput(processedValue),
                                      });
                                    }}
                                    onBlur={(e) => {
                                      const formattedValue =
                                        formatCurrencyOnBlur(editForm.balance);
                                      const numericValue =
                                        getCurrencyValue(formattedValue);
                                      setEditForm({
                                        ...editForm,
                                        balance: String(numericValue),
                                        balanceFormatted: formattedValue,
                                      });
                                    }}
                                    onFocus={(e) => {
                                      if (e.target.value === "$0.00") {
                                        e.target.select();
                                      }
                                    }}
                                    fullWidth
                                    size="small"
                                  />
                                </TableCell>
                                {config.showRiskTolerance && (
                                  <TableCell>
                                    {isPrimaryResidence(asset) ? (
                                      <TextField
                                        disabled
                                        value="-"
                                        fullWidth
                                        size="small"
                                      />
                                    ) : (
                                      <TextField
                                        type="number"
                                        inputProps={{ min: 1, max: 99 }}
                                        value={editForm.riskTolerance || ""}
                                        onChange={(e) =>
                                          setEditForm({
                                            ...editForm,
                                            riskTolerance: e.target.value,
                                          })
                                        }
                                        fullWidth
                                        size="small"
                                        placeholder="1-99"
                                      />
                                    )}
                                  </TableCell>
                                )}
                                {config.showLastUpdated && (
                                  <TableCell>
                                    <TextField
                                      type="date"
                                      value={editForm.lastUpdated || ""}
                                      disabled
                                      fullWidth
                                      size="small"
                                    />
                                  </TableCell>
                                )}
                                {config.editable && (
                                  <TableCell>
                                    <Stack direction="row" spacing={1}>
                                      <Button
                                        variant="contained"
                                        color="success"
                                        size="small"
                                        onClick={handleSaveEdit}
                                      >
                                        Save
                                      </Button>
                                      <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        onClick={handleCancelEdit}
                                      >
                                        Cancel
                                      </Button>
                                    </Stack>
                                  </TableCell>
                                )}
                              </>
                            ) : (
                              // Display Mode
                              <>
                                {config.showMember && (
                                  <TableCell 
                                    sx={{ 
                                      color: isDarkMode ? 'text.primary' : 'inherit',
                                      bgcolor: isDarkMode ? 'background.paper' : 'inherit',
                                      borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : undefined
                                    }}
                                  >
                                    {getOwnerOrMemberName(asset)}
                                  </TableCell>
                                )}
                                <TableCell 
                                  sx={{ 
                                    color: isDarkMode ? 'text.primary' : 'inherit',
                                    bgcolor: isDarkMode ? 'background.paper' : 'inherit',
                                    borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : undefined
                                  }}
                                >
                                  {isPrimaryResidence(asset)
                                    ? "Primary Residence"
                                    : getFinancialInstitutionName(
                                        asset.financialInstitutionId,
                                        asset,
                                      )}
                                </TableCell>
                                <TableCell 
                                  sx={{ 
                                    color: isDarkMode ? 'text.primary' : 'inherit',
                                    bgcolor: isDarkMode ? 'background.paper' : 'inherit',
                                    borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : undefined
                                  }}
                                >
                                  {isPrimaryResidence(asset)
                                    ? "Primary Residence"
                                    : getAssetTypeName(asset.assetType)}
                                </TableCell>
                                <TableCell 
                                  sx={{ 
                                    color: isDarkMode ? 'text.primary' : 'inherit',
                                    bgcolor: isDarkMode ? 'background.paper' : 'inherit',
                                    borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : undefined
                                  }}
                                >
                                  {isPrimaryResidence(asset)
                                    ? "-"
                                    : getTaxQualificationName(
                                        asset.taxQualification,
                                      )}
                                </TableCell>
                                <TableCell 
                                  sx={{ 
                                    color: isDarkMode ? 'text.primary' : 'inherit',
                                    bgcolor: isDarkMode ? 'background.paper' : 'inherit',
                                    borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : undefined
                                  }}
                                >
                                  {isPrimaryResidence(asset)
                                    ? "-"
                                    : getAccountTypeName(
                                        asset.accountType,
                                        asset,
                                      )}
                                </TableCell>
                                <TableCell 
                                  align="right"
                                  sx={{ 
                                    color: isDarkMode ? 'text.primary' : 'inherit',
                                    bgcolor: isDarkMode ? 'background.paper' : 'inherit',
                                    borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : undefined
                                  }}
                                >
                                  <Typography 
                                    fontWeight="500" 
                                    sx={{ 
                                      color: isDarkMode ? 'text.primary' : 'inherit'
                                    }}
                                  >
                                    {formatAsCurrency(asset.balance || 0)}
                                  </Typography>
                                </TableCell>
                                {config.showRiskTolerance && (
                                  <TableCell 
                                    align="center"
                                    sx={{ 
                                      color: isDarkMode ? 'text.primary' : 'inherit',
                                      bgcolor: isDarkMode ? 'background.paper' : 'inherit',
                                      borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : undefined
                                    }}
                                  >
                                    {isPrimaryResidence(asset) ? (
                                      "-"
                                    ) : asset.riskTolerance ? (
                                      <Chip
                                        label={asset.riskTolerance}
                                        size="small"
                                        color={
                                          asset.riskTolerance > 75
                                            ? "error"
                                            : asset.riskTolerance > 50
                                              ? "warning"
                                              : asset.riskTolerance > 25
                                                ? "info"
                                                : "success"
                                        }
                                        sx={{
                                          color: isDarkMode ? 'white' : undefined,
                                          '& .MuiChip-label': {
                                            color: isDarkMode ? 'white' : undefined
                                          }
                                        }}
                                      />
                                    ) : (
                                      "-"
                                    )}
                                  </TableCell>
                                )}
                                {config.showLastUpdated && (
                                  <TableCell 
                                    sx={{ 
                                      color: isDarkMode ? 'text.primary' : 'inherit',
                                      bgcolor: isDarkMode ? 'background.paper' : 'inherit',
                                      borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : undefined
                                    }}
                                  >
                                    {asset.lastUpdated || "-"}
                                  </TableCell>
                                )}
                                {config.editable && (
                                  <TableCell 
                                    align="right"
                                    sx={{ 
                                      color: isDarkMode ? 'text.primary' : 'inherit',
                                      bgcolor: isDarkMode ? 'background.paper' : 'inherit',
                                      borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : undefined
                                    }}
                                  >
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      justifyContent="flex-end"
                                    >
                                      <Tooltip title="Edit">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleEdit(asset)}
                                          sx={{
                                            color: isDarkMode ? 'primary.light' : 'primary.main'
                                          }}
                                        >
                                          <EditIcon />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Delete">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleDelete(asset.id)}
                                          sx={{
                                            color: isDarkMode ? 'error.light' : 'error.main'
                                          }}
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </Stack>
                                  </TableCell>
                                )}
                              </>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Paper>
            );
          })}

          {/* Total Assets Summary Card */}
          <Card
            sx={{
              mt: 3,
              p: 2,
              bgcolor: isDarkMode ? 'primary.dark' : theme.palette.primary.main,
              color: "white",
              borderRadius: 2,
              boxShadow: isDarkMode ? '0 4px 20px 0 rgba(0,0,0,0.5)' : undefined
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography 
                variant="h6"
                sx={{
                  color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'white'
                }}
              >
                Total Assets
              </Typography>
              <Typography 
                variant="h5" 
                fontWeight="600"
                sx={{
                  color: 'white'
                }}
              >
                {formatAsCurrency(totalAssets)}
              </Typography>
            </Stack>
          </Card>
        </CardContent>
      </Card>
    </Box>
  );
};

// Helper function to map database asset to component format
export const mapDatabaseAssetToDisplay = (
  asset,
  financialInstitutions = [],
  assetTypes = [],
  taxQualifications = [],
  accountTypes = [],
) => {
  // Find related entity names based on IDs
  const financialInstitution = financialInstitutions.find(
    (fi) => fi.institution_id === asset.financial_institution_id,
  );
  const assetType = assetTypes.find(
    (at) => at.asset_type_id === asset.asset_type_id,
  );
  const taxQualification = taxQualifications.find(
    (tq) => tq.tax_qualification_id === asset.tax_qualification_id,
  );
  const foundAccountType = accountTypes.find(
    (at) => at.account_type_id === asset.account_type_id,
  );

  // Use appropriate value field based on asset type
  const balance =
    asset.approximate_value || asset.checking_savings_balance || 0;

  return {
    id:
      asset.asset_id ||
      asset.id ||
      `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
    memberId: asset.user_id || null,
    financialInstitution: financialInstitution?.institution_name || "",
    financialInstitutionId: asset.financial_institution_id || null,
    assetType: asset.asset_type_id || "",
    assetTypeName: assetType?.asset_type_name || "",
    taxQualification: asset.tax_qualification_id || "",
    taxQualificationName: taxQualification?.tax_qualification_name || "",
    accountType: asset.account_type_id || "",
    accountTypeName: foundAccountType?.account_type_name || "",
    balance,
    ownerType: asset.owner_type_id?.toString() || "",
    ownership_structure_id: asset.ownership_structure_id || null,
    secondary_owner_user_id: asset.secondary_owner_user_id || null,
    riskTolerance: asset.overall_portfolio_risk_number || "",
    accountNumber: asset.account_number || "",
    isInternal: asset.is_internal || false,
    interestedInPortfolioAnalysis:
      asset.interested_in_portfolio_analysis || false,
    additionalDetails: asset.additional_asset_details || "",
    expectedRateOfReturn: asset.expected_rate_of_return || "",
    created_at: asset.created_at || new Date().toISOString(),
    updated_at: asset.updated_at || new Date().toISOString(),
    lastUpdated: asset.lastUpdated || new Date().toISOString().split("T")[0],
  };
};

// Reverse mapping function to convert component format back to database format
export const mapDisplayAssetToDatabase = (asset) => {
  const currentTimestamp = new Date().toISOString();

  return {
    asset_id:
      asset.id && !asset.id.toString().startsWith("temp-")
        ? asset.id
        : undefined,
    user_id: asset.memberId,
    financial_institution_id: asset.financialInstitutionId,
    asset_type_id: asset.assetType,
    tax_qualification_id: asset.taxQualification,
    account_type: asset.accountType,
    approximate_value: parseFloat(asset.balance),
    owner_type_id: asset.ownerType,
    overall_portfolio_risk_number: asset.riskTolerance,
    account_number: asset.accountNumber,
    is_internal: asset.isInternal,
    interested_in_portfolio_analysis: asset.interestedInPortfolioAnalysis,
    additional_asset_details: asset.additionalDetails,
    expected_rate_of_return: asset.expectedRateOfReturn,
    updated_at: asset.updated_at || currentTimestamp,
    created_at: asset.created_at || currentTimestamp,
    lastUpdated: asset.lastUpdated || new Date().toISOString().split("T")[0],
  };
};

export default AssetAnalysisTable;
